import ChevronDownIcon from '@heroicons/react/20/solid/ChevronDownIcon.js';
import PlusIcon from '@heroicons/react/20/solid/PlusIcon.js';
import XMarkIcon from '@heroicons/react/24/outline/XMarkIcon.js';
import React from 'react';
import { useCollator, useFilter, useObjectRef } from 'react-aria';
import { useIsHidden } from 'react-aria/private/collections/Hidden';
import {
  Button as AriaButton,
  ButtonContext as AriaButtonContext,
  ComboBox as AriaComboBox,
  ComboBoxStateContext,
  Group,
  Input as AriaInput,
  type Key,
  ListBoxItem as AriaListBoxItem,
} from 'react-aria-components';

import { forwardRef } from '../../generic-forward-ref';
import { tv } from '../../tv';
import { FieldAction, FieldActionContext, fieldSeatClass } from '../field/field-action';
import {
  adornmentStyles,
  entryStyles,
  FieldLabel,
  FieldMessage,
  focusEntryOnPress,
  frameState,
  frameStyles,
  resolveFieldState,
  useFieldLayout,
  useFieldMessageIds,
} from '../field/field-shell';
import { type PickerValue, samePickerValue, useOwnedState } from '../field/owned-state';
import { type ChipEntry, PickerChips } from '../list-box/chips';
import { type OptionItemsProps, OptionList, RETRY_LOAD_MORE_KEY } from '../list-box/list-box';
import { type OptionMeta, readOptionMeta } from '../list-box/option';
import {
  ListSuppressionContext,
  useListSuppression,
  withoutSuppressedToggles,
} from '../list-box/suppression';
import { PickerPopup } from '../overlay/picker-popup';
import { rowStyles } from '../overlay/row';
import { Spinner } from '../spinner/spinner';

/* The frame's spinner waits half a second (loading.md): a fast answer never
 * flickers, a slow one is acknowledged. The list's spinner row does not: a
 * message saying nothing matches would be a lie while the answer is pending. */
const LOADING_DELAY_MS = 500;

const comboboxStyles = tv({
  slots: {
    loading: 'ck:flex ck:shrink-0 ck:items-center ck:text-text-muted',
    chipsRow: 'ck:flex ck:min-w-0 ck:flex-1 ck:flex-wrap ck:items-center ck:gap-1',
    /* The entry in the chips row takes the row's height and keeps a few
     * characters' room beside the chips before it wraps under them. */
    chipsEntry: 'ck:h-auto ck:min-w-12',
  },
});

interface ComboboxCommonProps {
  /**
   * Help under the field: the rule the value must meet, or why the field is
   * read-only. Inline content only (Text, Link, Code runs), never blocks.
   * Hidden while `error` shows, since the error restates the rule.
   */
  description?: React.ReactNode;
  /**
   * The error message. The field is invalid exactly when this is non-empty.
   * Say what to do: "Choose a vendor", never "Invalid value".
   */
  error?: string;
  /** Marks the label with an asterisk and exposes `aria-required`. */
  isRequired?: boolean;
  /** Washed out and skipped by Tab, its clear with it. Prefer `isReadOnly` whenever the value still matters. */
  isDisabled?: boolean;
  /** Full contrast and focusable, but not editable: the list never opens. Say why in `description`. */
  isReadOnly?: boolean;
  /** Example input, never a substitute for the label. Absent, the entry is blank. */
  placeholder?: string;
  /** The form field name; the value is submitted through hidden inputs, one per chosen id. */
  name?: string;
  /** The id of the input element. */
  id?: string;
  /** Whether the input takes focus on mount. */
  autoFocus?: boolean;
  /** Called when the input loses focus. */
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  /** Called when the input receives focus. */
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  /**
   * Called when the list opens or closes. The list opens from a press in
   * the entry, from typing, ArrowDown or the chevron, never from focus alone
   * and never from a prop: React Aria's ComboBox has no controlled open.
   */
  onOpenChange?: (isOpen: boolean) => void;
  /** Values the render function closes over, so a change re-renders the options. */
  dependencies?: unknown[];
  /** Height on the Button ramp: md 32px, lg 40px. The clear and the toggle take seats a 24px frame has no room for. */
  size?: 'md' | 'lg';
  /**
   * The typed text (controlled). With it, the caller narrows the options:
   * pass the matches as `items`. Pair it with `onInputChange`; alone, it
   * freezes the entry.
   */
  inputValue?: string;
  /** The typed text on mount (uncontrolled); a chosen option's label wins in the single form. */
  defaultInputValue?: string;
  /**
   * Called with the text on every keystroke; with the chosen option's label
   * on a pick or when a value arrives; and with the label again, or an empty
   * text, when the list closes without a pick. In the multiple form the text
   * is the search alone: it empties when the field is left, and on a pick
   * while the picker narrows its own list. A caller's text is the caller's:
   * a server search keeps it across picks, so several results of one query
   * can be picked.
   * Narrowing stays the picker's unless `inputValue` is controlled too. A
   * server search skips the text while it is the chosen label: that is the
   * entry naming the value, not a search. A pick reports `onChange` first,
   * then the label here.
   */
  onInputChange?: (text: string) => void;
  /**
   * Options are on their way: an empty list shows a spinner row at once
   * instead of `emptyMessage`, and after half a second a spinner takes the
   * frame's end seat. The options already shown stay until the new ones
   * land. Meaningful with `items`, which is where options arrive.
   */
  isLoading?: boolean;
  /**
   * Called as the end of the list scrolls near view (within one list height),
   * for a server search that pages its matches. Not called while `isLoading`
   * or `isLoadingMore`.
   */
  onLoadMore?: () => void;
  /**
   * The next page is on its way: a spinner row after the options. Drop it in
   * the same update that adds the page, or the end still in view asks again.
   */
  isLoadingMore?: boolean;
  /**
   * The next page failed: paging stops and the list ends in a row with this
   * message ("Couldn't load more. Try again."), which asks for the
   * same page again through `onLoadMore` when pressed. Clear it once a page
   * lands or the text changes.
   */
  loadMoreError?: string;
  /** Shown in place of the options when the text matches none. */
  emptyMessage: string;
}

interface ComboboxLabelledProps {
  /** The visible name, above the field or beside it (Form decides). */
  label: string;
  'aria-label'?: never;
  'aria-labelledby'?: never;
}

interface ComboboxUnlabelledProps {
  label?: never;
  /** The accessible name of a picker with no visible label (a toolbar search). */
  'aria-label': string;
  'aria-labelledby'?: never;
}

interface ComboboxLabelledByProps {
  label?: never;
  'aria-label'?: never;
  /** The id of the visible element that names the field (a question's text). */
  'aria-labelledby': string;
}

interface ComboboxSingleValueProps {
  /** One choice (the default), or several shown as chips before the entry. */
  selectionMode?: 'single';
  /** The chosen option's id, or null for none; in the multiple form, the chosen ids in the order chosen (controlled). */
  value?: string | null;
  /** The value on mount (uncontrolled), restored by a form reset. */
  defaultValue?: string | null;
  /** Called with the new value: the chosen id, or null when cleared or the entry is emptied; in the multiple form, the chosen ids. */
  onChange?: (value: string | null) => void;
  removeLabel?: never;
}

interface ComboboxMultipleProps {
  /**
   * Several choices, shown as chips before the entry that wrap and grow the
   * frame; the list stays open while picking. Backspace in an empty entry
   * moves to the last chip, and a second Backspace removes it.
   */
  selectionMode: 'multiple';
  /** The chosen option's id, or null for none; in the multiple form, the chosen ids in the order chosen (controlled). */
  value?: string[];
  /** The value on mount (uncontrolled), restored by a form reset. */
  defaultValue?: string[];
  /** Called with the new value: the chosen id, or null when cleared or the entry is emptied; in the multiple form, the chosen ids. */
  onChange?: (value: string[]) => void;
  /** Names each chip's remove button from its label ("Remove Ana Souza"). Required with multiple. */
  removeLabel: (label: string) => string;
  /** Each chip is its own clear. */
  isClearable?: never;
  clearLabel?: never;
}

interface ComboboxUnclearableProps {
  isClearable?: false;
  clearLabel?: never;
}

interface ComboboxClearableProps {
  /**
   * Seats a clear once there is a value or text; it empties both, reports
   * `onChange(null)` and keeps focus in the entry, so the list opens for
   * the next pick.
   */
  isClearable: true;
  /** Names the icon-only clear ("Clear vendor"). Required with `isClearable`. */
  clearLabel: string;
}

interface ComboboxUncreatableProps {
  onCreate?: never;
  createLabel?: never;
}

interface ComboboxCreatableProps {
  /**
   * Text that matches no option offers a last row that creates it. Called
   * with the text; add the new option to `items` and choose it through
   * `value`. The row is withheld while a returned promise is pending.
   */
  onCreate: (text: string) => void | Promise<void>;
  /** The create row's label from the text ('Create "Acme"'). Required with `onCreate`. */
  createLabel: (text: string) => string;
}

export type ComboboxProps<T = object> = ComboboxCommonProps &
  OptionItemsProps<T> &
  (ComboboxLabelledProps | ComboboxUnlabelledProps | ComboboxLabelledByProps) &
  (
    | (ComboboxSingleValueProps & (ComboboxUnclearableProps | ComboboxClearableProps))
    | ComboboxMultipleProps
  ) &
  (ComboboxUncreatableProps | ComboboxCreatableProps);

/** What the picker remembers of an option once it has been seen. */
interface KnownOption {
  label: string;
  meta?: OptionMeta;
}

/* True once the flag has been up for the delay; down at once. */
function useDelayedFlag(flag: boolean, delayMs: number) {
  const [delayed, setDelayed] = React.useState(false);
  React.useEffect(() => {
    if (!flag) {
      setDelayed(false);
      return;
    }
    const timer = setTimeout(() => setDelayed(true), delayMs);
    return () => clearTimeout(timer);
  }, [flag, delayMs]);
  return delayed;
}

type ComboboxState = NonNullable<React.ContextType<typeof ComboBoxStateContext>>;

/* Escape is React Aria's rule: while the list is open, or while the text is
 * not the chosen option's label (an edit to revert, or text with no choice),
 * the key acts here and must not travel on to a Chakra host that would close
 * itself. Once there is nothing left for it to do it travels, so a second
 * Escape still closes the host. */
const ComboboxInput = React.forwardRef<
  HTMLInputElement,
  React.ComponentProps<typeof AriaInput> & {
    chosenLabel: string;
    onKeyDownFirst: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  }
>(function ComboboxInput({ chosenLabel, onKeyDownFirst, ...props }, ref) {
  const state = React.useContext(ComboBoxStateContext);
  return (
    <AriaInput
      {...props}
      ref={ref}
      onKeyDownCapture={onKeyDownFirst}
      onKeyDown={(e) => {
        if (e.key === 'Escape' && state) {
          if (state.isOpen || state.inputValue !== chosenLabel) e.stopPropagation();
        }
        props.onKeyDown?.(e);
      }}
    />
  );
});

/* With both the value and the text controlled, React Aria leaves the text
 * to the picker. The text follows the picker's own value, never React Aria's
 * view of it, which loses the chosen key whenever a caller's narrowed items
 * leave it out: when the value moves (a pick already wrote the label; a value
 * from outside, a reset, a load on mount did not), and when a missing label
 * first arrives with nothing typed since, focused or not (a form that links a
 * record once it has loaded). A write while the entry is focused goes through
 * React Aria's revert, which records the text it settles on; a bare write
 * would read as typing and reopen the list. */
function ComboboxTextSync({
  current,
  text,
  setText,
  stateRef,
  knownRef,
}: {
  current: string | null;
  text: string;
  setText: (text: string) => void;
  stateRef: React.MutableRefObject<ComboboxState | null>;
  knownRef: React.MutableRefObject<Map<string, KnownOption>>;
}) {
  /* Subscribed so the label is seen when React Aria's collection fills, a
   * render after the first; the bridge has recorded it by then. */
  React.useContext(ComboBoxStateContext);
  const isHidden = useIsHidden();
  const label = current == null ? '' : (knownRef.current.get(current)?.label ?? null);
  const previous = React.useRef<string | null | undefined>(undefined);
  /* The text as the value moved while its label was still missing: the
   * label lands only if nobody has typed since. */
  const awaitingLabel = React.useRef<{ text: string } | null>(null);
  React.useEffect(() => {
    const state = stateRef.current;
    if (isHidden || !state) return;
    const isMount = previous.current === undefined;
    const moved = !isMount && previous.current !== current;
    previous.current = current;
    let next: string | null = null;
    if (moved || (isMount && current != null)) {
      awaitingLabel.current = label == null ? { text } : null;
      next = label;
    } else if (awaitingLabel.current && label != null) {
      const untouched = awaitingLabel.current.text === text;
      awaitingLabel.current = null;
      if (untouched) next = label;
    }
    if (next == null || next === text) return;
    if (state.isFocused) state.revert();
    else setText(next);
  });
  return null;
}

/* The chosen options as chips, read from what the bridge remembers;
 * subscribed to React Aria's state so they appear once the collection that
 * names them fills. An id never seen among the items shows as itself. */
function ComboboxChips({
  ids,
  knownRef,
  ...chips
}: Omit<React.ComponentProps<typeof PickerChips>, 'entries'> & {
  ids: string[];
  knownRef: React.MutableRefObject<Map<string, KnownOption>>;
}) {
  React.useContext(ComboBoxStateContext);
  if (ids.length === 0) return null;
  const entries: ChipEntry[] = ids.map((id) => {
    const known = knownRef.current.get(id);
    return { id, label: known?.label ?? id, meta: known?.meta };
  });
  return <PickerChips {...chips} entries={entries} />;
}

/* Lends React Aria's state to the handlers, which run outside its context,
 * and remembers every chosen option it can see (label and look), so a chip
 * or the entry outlives a caller's narrowed items. The hidden collection
 * pass renders this too, with no state; it is skipped. */
function ComboboxStateBridge({
  stateRef,
  knownRef,
}: {
  stateRef: React.MutableRefObject<ComboboxState | null>;
  knownRef: React.MutableRefObject<Map<string, KnownOption>>;
}) {
  const state = React.useContext(ComboBoxStateContext);
  const isHidden = useIsHidden();
  if (state && !isHidden) {
    for (const node of state.selectedItems) {
      knownRef.current.set(String(node.key), {
        label: node.textValue,
        meta: readOptionMeta(node.props),
      });
    }
  }
  React.useLayoutEffect(() => {
    if (!isHidden) stateRef.current = state ?? null;
  });
  return null;
}

/* Whether the text already names an option, compared the way React Aria
 * narrows (accents and case aside), so the create row has nothing to add.
 * Read from the collection, which only exists inside the ComboBox, and
 * handed up before paint. */
function ComboboxExactMatch({
  text,
  createId,
  onChange,
}: {
  text: string;
  createId: string;
  onChange: (hasExact: boolean) => void;
}) {
  const state = React.useContext(ComboBoxStateContext);
  const isHidden = useIsHidden();
  const collator = useCollator({ sensitivity: 'base', usage: 'search' });
  let hasExact = false;
  if (state && text && !isHidden) {
    for (const key of state.collection.getKeys()) {
      const node = state.collection.getItem(key);
      if (node?.type !== 'item' || String(key) === createId) continue;
      if (collator.compare(node.textValue, text) === 0) {
        hasExact = true;
        break;
      }
    }
  }
  React.useLayoutEffect(() => {
    if (!isHidden) onChange(hasExact);
  }, [hasExact, onChange, isHidden]);
  return null;
}

function ComboboxPopup({
  frameRef,
  popupRef,
  children,
}: {
  frameRef: React.RefObject<HTMLDivElement>;
  popupRef: React.RefObject<HTMLElement>;
  children: React.ReactNode;
}) {
  const state = React.useContext(ComboBoxStateContext);
  return (
    <PickerPopup
      ref={popupRef}
      name="combobox"
      isOpen={state?.isOpen ?? false}
      triggerRef={frameRef}
    >
      {children}
    </PickerPopup>
  );
}

/* The create row: the plus glyph in the checkmark column before the caller's
 * label. Its text value is the raw typed text, so React Aria's own narrowing
 * always keeps it. */
function CreateOption({ id, text, label }: { id: string; text: string; label: string }) {
  const { item, itemCheck, itemLabel } = rowStyles({ truncate: true });
  return (
    <AriaListBoxItem id={id} textValue={text} className={item()} data-component="create-option">
      <PlusIcon aria-hidden="true" focusable="false" className={itemCheck({ selected: true })} />
      <span className={itemLabel()}>{label}</span>
    </AriaListBoxItem>
  );
}

/**
 * A text entry whose list is what the text matches: the field anatomy
 * around an input, the options narrowed as the user types, from a list the
 * picker holds or from a server that answers the text (field.md,
 * picker.md). The value is the chosen Option's `id`; the text is the
 * option's label once chosen. With `selectionMode="multiple"`, several
 * choices as removable chips before the entry, the value their ids in the
 * order chosen and the text the search alone. Flat props, the Form deciding
 * where the label sits, the error replacing the description, `aria`
 * validation.
 *
 * The forwarded ref is the `<input>`, so a form library focusing the first
 * invalid field lands on it. No `onKeyDown`: React Aria owns the entry's
 * keys (arrows, Enter, Escape) and a caller has the value and the text.
 */
export const Combobox = forwardRef(function Combobox<T extends object>(
  {
    label,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    selectionMode = 'single',
    removeLabel,
    description,
    error: errorProp,
    isRequired: requiredProp,
    isDisabled: disabledProp,
    isReadOnly: readOnlyProp,
    placeholder,
    size = 'md',
    isClearable,
    clearLabel,
    onCreate,
    createLabel,
    value,
    defaultValue,
    onChange,
    inputValue,
    defaultInputValue,
    onInputChange,
    isLoading = false,
    onLoadMore,
    isLoadingMore,
    loadMoreError,
    emptyMessage,
    onOpenChange,
    onBlur,
    onFocus,
    items,
    children,
    dependencies,
    ...rest
  }: ComboboxProps<T>,
  ref: React.ForwardedRef<HTMLInputElement>,
) {
  const { error, isRequired, isDisabled, isReadOnly, isInvalid } = resolveFieldState({
    error: errorProp,
    isRequired: requiredProp,
    isDisabled: disabledProp,
    isReadOnly: readOnlyProp,
  });
  const inputRef = useObjectRef(ref);
  const frameRef = React.useRef<HTMLDivElement>(null);
  const popupRef = React.useRef<HTMLElement>(null);
  const createId = React.useId();
  const { labelPosition, styles } = useFieldLayout(size);
  const { root, label: labelClass, column } = styles;
  const isInert = isDisabled || isReadOnly;
  const { loading, chipsRow, chipsEntry } = comboboxStyles();
  const { icon } = adornmentStyles({ size });
  const { descriptionId, errorId, describedBy } = useFieldMessageIds(description, error);
  const { suppressed, context: suppression } = useListSuppression();
  const showsSpinner = useDelayedFlag(isLoading, LOADING_DELAY_MS);

  const isMultiple = selectionMode === 'multiple';
  const chipsId = React.useId();
  /* React Aria restores its own default on a native form reset; the picker
   * hands it the value it started with, so a reset lands on that. */
  const [initialValue] = React.useState<PickerValue>(
    () => defaultValue ?? value ?? (selectionMode === 'multiple' ? [] : null),
  );
  const [current, setCurrent] = useOwnedState<PickerValue>(
    value,
    defaultValue ?? (isMultiple ? [] : null),
    onChange as ((next: PickerValue) => void) | undefined,
    samePickerValue,
  );
  const single = Array.isArray(current) ? null : current;
  const chosen = Array.isArray(current) ? current : [];
  const [text, setText] = useOwnedState<string>(inputValue, defaultInputValue ?? '', onInputChange);
  const narrowsItself = inputValue === undefined;
  const stateRef = React.useRef<ComboboxState | null>(null);
  const chipsRef = React.useRef<HTMLDivElement>(null);
  /* The chosen options as last seen, kept so the entry and the chips can be
   * restored when a caller's narrowed items no longer hold them. */
  const knownRef = React.useRef(new Map<string, KnownOption>());
  const chosenLabel = single == null ? '' : (knownRef.current.get(single)?.label ?? '');

  /* React Aria nulls the key on an emptied entry only while it owns the
   * text; the picker owns it, so the rule lives here. */
  const handleInputChange = (next: string) => {
    setText(next);
    if (next === '' && single != null) setCurrent(null);
  };

  const trimmed = text.trim();
  const { contains } = useFilter({ sensitivity: 'base' });
  /* Narrowing reads the text as the create row and Enter do, trimmed, so a
   * trailing space never hides the option the text names. */
  const narrowTrimmed = React.useCallback(
    (textValue: string, input: string) => contains(textValue, input.trim()),
    [contains],
  );
  const [hasExactMatch, setHasExactMatch] = React.useState(false);
  const [isCreating, setCreating] = React.useState(false);
  /* Never over a list still on its way: the match it would duplicate may be
   * in the answer. */
  const offersCreate =
    !!onCreate && trimmed.length > 0 && !hasExactMatch && !isCreating && !isLoading;

  const runCreate = () => {
    if (!onCreate || isCreating) return;
    const result = onCreate(trimmed);
    /* The value does not move on a create, so React Aria does not close the
     * list on its own; its close() commits, which would wipe the typed text,
     * so a single picker toggles it shut. A multiple one stays open for the
     * next pick, its search emptied. */
    const state = stateRef.current;
    if (isMultiple) setText('');
    else if (state?.isOpen) state.toggle(null);
    if (result instanceof Promise) {
      setCreating(true);
      const settle = () => setCreating(false);
      void result.then(settle, settle);
    }
  };

  const remember = (id: string) => {
    const node = stateRef.current?.collection.getItem(id);
    if (node) knownRef.current.set(id, { label: node.textValue, meta: readOptionMeta(node.props) });
  };

  /* The multiple form's change: a pick or a removal moves the ids and, when
   * the picker narrows its own list, empties the search for the next one;
   * a caller's search is the caller's, kept so several of its results can be
   * picked. React Aria's commit on a blur or an Escape repeats the value it
   * holds, which the equality guard drops. */
  const handleMultipleChange = (keys: Key[]) => {
    const ids = keys.map(String);
    if (ids.includes(RETRY_LOAD_MORE_KEY)) {
      onLoadMore?.();
      return;
    }
    if (ids.includes(createId)) {
      runCreate();
      return;
    }
    const next = withoutSuppressedToggles(ids, chosen, suppressed.current);
    if (samePickerValue(next, chosen)) return;
    next.forEach(remember);
    setCurrent(next);
    if (narrowsItself) setText('');
  };

  const handleSelectionChange = (key: Key | null) => {
    if (key != null && String(key) === RETRY_LOAD_MORE_KEY) {
      onLoadMore?.();
      return;
    }
    if (key != null && String(key) === createId) {
      runCreate();
      return;
    }
    const next = key == null ? null : String(key);
    if (next != null && suppressed.current.has(next)) return;
    /* Fired on a pick, and on Escape or blur with the key React Aria already
     * holds, which expects the text synced back to that item's label (empty
     * for none): a text left behind reads as typing and reopens the list.
     * A null here while a value is chosen means React Aria lost the chosen
     * key from a caller's narrowed items: a revert, never a clear. */
    const state = stateRef.current;
    if (next == null && single != null) {
      setText(knownRef.current.get(single)?.label ?? text);
      return;
    }
    if (next != null) remember(next);
    const label =
      next == null
        ? ''
        : (state?.collection.getItem(next)?.textValue ?? knownRef.current.get(next)?.label ?? text);
    /* The value first, as the multiple form does: a caller hears what was
     * picked before the text names it, so it can tell the name from a search. */
    setCurrent(next);
    setText(label);
  };

  /* Focus returns to the entry only from inside the field (a chip removed
   * by Backspace): a pointer remove leaves focus where it was. */
  const removeChips = isInert
    ? undefined
    : (ids: string[]) => {
        const wasInside = frameRef.current?.contains(document.activeElement) ?? false;
        setCurrent(chosen.filter((id) => !ids.includes(id)));
        if (wasInside) inputRef.current?.focus();
      };

  /* A chip is a stop on the way back to the entry: Escape, ArrowRight past
   * the last chip and any character typed return to it (the character
   * lands there), so a user who reached a chip by Backspace is never stuck
   * on it, and Escape never travels on to a host from a chip. */
  const handleChipKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    const rows = chipsRef.current?.querySelectorAll<HTMLElement>('[role="row"]');
    const isLast = rows != null && rows[rows.length - 1]?.contains(target);
    const isCharacter = e.key.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey;
    if (e.key === 'Escape' || (e.key === 'ArrowRight' && isLast)) {
      e.preventDefault();
      e.stopPropagation();
      inputRef.current?.focus();
    } else if (isCharacter && e.key !== ' ') {
      inputRef.current?.focus();
    }
  };

  /* React Aria's key handlers read the list as of the last render, so the
   * two keys the picker decides differently are settled before they reach
   * it. Enter with no option arrowed to picks the one the text names: an
   * exact match, or the only row (a create row included); with none, it
   * keeps the text rather than reverting it. Tab never picks: it leaves
   * the field, and the blur reverts uncommitted text. While a create is
   * pending, Enter does nothing (a second commit is ignored, rule 17). */
  const collator = useCollator({ sensitivity: 'base', usage: 'search' });
  const handleKeyDownFirst = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const state = stateRef.current;
    if (!state || e.nativeEvent.isComposing) return;
    /* The multiple form's Escape is staged like SearchField's: close the
     * list keeping the search, then empty the search, then let it travel.
     * React Aria's own revert would reopen a list over a kept search. */
    if (isMultiple && e.key === 'Escape') {
      if (state.isOpen) {
        e.stopPropagation();
        state.toggle(null);
      } else if (text) {
        e.stopPropagation();
        setText('');
        state.revert();
      }
      return;
    }
    /* Backspace in an empty entry moves to the last chip; the chip's own
     * Backspace then removes it. A single press never deletes. */
    if (isMultiple && e.key === 'Backspace' && text === '' && chosen.length > 0 && !isInert) {
      const rows = chipsRef.current?.querySelectorAll<HTMLElement>('[role="row"]');
      const last = rows?.[rows.length - 1];
      if (last) {
        e.preventDefault();
        e.stopPropagation();
        last.focus();
      }
      return;
    }
    /* In the multiple form React Aria's commit settles an empty text over a
     * kept search and reopens the list, so Tab leaves to the blur. */
    if (
      e.key === 'Tab' &&
      state.isOpen &&
      (state.selectionManager.focusedKey != null || isMultiple)
    ) {
      e.stopPropagation();
      return;
    }
    if (e.key !== 'Enter') return;
    if (isCreating) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    if (!state.isOpen) {
      /* Closed over a kept search, React Aria's commit would reopen it; the
       * key still travels with its default, so a form still submits. */
      if (isMultiple) e.stopPropagation();
      return;
    }
    if (state.selectionManager.focusedKey != null) return;
    const rows = [...state.collection.getKeys()]
      .map((key) => state.collection.getItem(key))
      .filter(
        (node) =>
          node?.type === 'item' &&
          !suppressed.current.has(String(node.key)) &&
          !state.selectionManager.isDisabled(node.key),
      );
    const named =
      rows.find((node) => node && collator.compare(node.textValue, trimmed) === 0) ??
      (rows.length === 1 ? rows[0] : undefined);
    e.preventDefault();
    e.stopPropagation();
    if (!named) return;
    if (!isMultiple) handleSelectionChange(named.key);
    else if (String(named.key) === createId) runCreate();
    else if (chosen.includes(String(named.key))) {
      if (narrowsItself) setText('');
    } else handleMultipleChange([...chosen, named.key]);
  };

  /* React Aria reports a repeated open while the create row comes and goes
   * under the typing; the caller hears each change once. */
  const reportedOpen = React.useRef(false);
  const [isListOpen, setListOpen] = React.useState(false);
  const handleOpenChange = (isOpen: boolean) => {
    if (reportedOpen.current === isOpen) return;
    reportedOpen.current = isOpen;
    setListOpen(isOpen);
    onOpenChange?.(isOpen);
  };

  /* A press in the entry opens the list, as a Select's press does; focus
   * alone never does, so tabbing through a form opens nothing. The chevron,
   * the clear and a chip keep their own presses. */
  const openOnPress = (e: React.MouseEvent) => {
    if (e.button !== 0 || isInert) return;
    if (e.target instanceof Element && e.target.closest('button, [data-component="chip"]')) return;
    const state = stateRef.current;
    if (state && !state.isOpen) state.open(null, 'manual');
  };

  const showsClear =
    !!isClearable && !isMultiple && (single != null || text.length > 0) && !isInert;
  const seat = React.useMemo(
    () => ({ size, isDisabled, isTransient: true, keepsEntryFocus: true }),
    [size, isDisabled],
  );

  const createRow = offersCreate ? (
    <CreateOption id={createId} text={text} label={createLabel?.(trimmed) ?? trimmed} />
  ) : null;

  const field = (
    <AriaComboBox
      {...rest}
      {...(ariaLabel ? { 'aria-label': ariaLabel } : {})}
      {...(ariaLabelledBy ? { 'aria-labelledby': ariaLabelledBy } : {})}
      /* The one consumer of this prop is React Aria's narrowing switch: a
       * caller who controls the text narrows; the list renders the items. */
      items={narrowsItself ? undefined : items}
      defaultFilter={narrowTrimmed}
      {...(isMultiple
        ? {
            selectionMode: 'multiple' as const,
            value: chosen,
            defaultValue: Array.isArray(initialValue) ? initialValue : [],
            onChange: handleMultipleChange,
          }
        : {
            selectedKey: single,
            defaultSelectedKey: Array.isArray(initialValue) ? null : initialValue,
            onSelectionChange: handleSelectionChange,
          })}
      inputValue={text}
      onInputChange={handleInputChange}
      onOpenChange={handleOpenChange}
      menuTrigger="input"
      allowsEmptyCollection
      isRequired={isRequired}
      isDisabled={isDisabled}
      isReadOnly={isReadOnly}
      isInvalid={isInvalid}
      validationBehavior="aria"
      aria-describedby={
        [isMultiple && chosen.length > 0 ? chipsId : null, describedBy].filter(Boolean).join(' ') ||
        undefined
      }
      className="ck:contents"
    >
      <ComboboxStateBridge stateRef={stateRef} knownRef={knownRef} />
      {isMultiple ? null : (
        <ComboboxTextSync
          current={single}
          text={text}
          setText={setText}
          stateRef={stateRef}
          knownRef={knownRef}
        />
      )}
      {onCreate ? (
        <ComboboxExactMatch text={trimmed} createId={createId} onChange={setHasExactMatch} />
      ) : null}
      {label ? (
        <FieldLabel isRequired={isRequired} className={labelClass()}>
          {label}
        </FieldLabel>
      ) : null}
      <div className={column()}>
        <Group
          ref={frameRef}
          className={frameStyles({
            size,
            multiline: false,
            chips: isMultiple,
            hasChips: chosen.length > 0,
            // The chevron is a seat at the frame's end.
            hasAction: true,
            state: frameState({ isDisabled, isReadOnly, isInvalid }),
          })}
          onMouseDown={focusEntryOnPress(inputRef)}
          onClick={openOnPress}
        >
          {isMultiple ? (
            <div className={chipsRow()}>
              <ComboboxChips
                ids={chosen}
                knownRef={knownRef}
                id={chipsId}
                label={label ?? ariaLabel ?? ''}
                labelledBy={ariaLabelledBy}
                size={size}
                removeLabel={removeLabel}
                onRemove={removeChips}
                isDisabled={isDisabled}
                listRef={chipsRef}
                onKeyDownCapture={handleChipKeyDown}
                isInline
              />
              <ComboboxInput
                ref={inputRef}
                className={entryStyles({
                  size,
                  multiline: false,
                  readOnly: isReadOnly,
                  class: chipsEntry(),
                })}
                placeholder={chosen.length > 0 ? undefined : placeholder}
                onBlur={(e) => {
                  /* A press on an option passes focus through it and back
                   * to the entry: only leaving the field empties the search. */
                  const into = e.relatedTarget;
                  if (!(into instanceof Node && popupRef.current?.contains(into))) setText('');
                  onBlur?.(e);
                }}
                onFocus={onFocus}
                chosenLabel=""
                onKeyDownFirst={handleKeyDownFirst}
              />
            </div>
          ) : (
            <ComboboxInput
              ref={inputRef}
              /* A chosen value too long for the entry ends in an ellipsis, as a Select's
               * does; browsers drop it while the text is being edited. */
              className={entryStyles({
                size,
                multiline: false,
                readOnly: isReadOnly,
                class: 'ck:text-ellipsis',
              })}
              placeholder={placeholder}
              onBlur={onBlur}
              onFocus={onFocus}
              chosenLabel={chosenLabel}
              onKeyDownFirst={handleKeyDownFirst}
            />
          )}
          {showsClear ? (
            /* React Aria hands the toggle's props to any Button inside the
             * ComboBox; the clear is its own button and takes none of them. */
            <AriaButtonContext.Provider value={null}>
              <FieldActionContext.Provider value={seat}>
                <FieldAction
                  icon={XMarkIcon}
                  label={clearLabel}
                  onPress={() => {
                    setText('');
                    setCurrent(null);
                    inputRef.current?.focus();
                  }}
                />
              </FieldActionContext.Provider>
            </AriaButtonContext.Provider>
          ) : null}
          {showsSpinner && isListOpen ? (
            <span className={loading()}>
              <Spinner size="sm" />
            </span>
          ) : null}
          <AriaButton
            className={fieldSeatClass(size, {
              isInert,
              besideChips: isMultiple,
              afterSeat: showsClear && !(showsSpinner && isListOpen),
            })}
            data-component="combobox-toggle"
            data-tone="neutral"
          >
            <ChevronDownIcon aria-hidden="true" className={icon()} />
          </AriaButton>
        </Group>
        <FieldMessage
          description={description}
          error={error}
          descriptionId={descriptionId}
          errorId={errorId}
        />
      </div>
      <ListSuppressionContext.Provider value={suppression}>
        <ComboboxPopup frameRef={frameRef} popupRef={popupRef}>
          <OptionList
            items={items}
            dependencies={dependencies}
            emptyMessage={emptyMessage}
            isLoading={isLoading}
            onLoadMore={onLoadMore}
            isLoadingMore={isLoadingMore}
            loadMoreError={loadMoreError}
            trailing={createRow}
          >
            {children}
          </OptionList>
        </ComboboxPopup>
      </ListSuppressionContext.Provider>
    </AriaComboBox>
  );
  /* React Aria renders its collection's template beside the ComboBox's own
   * element; this box holds both, so a host spacing its children (a Chakra
   * Stack) sees one field. */
  return (
    <div
      className={root()}
      data-component="combobox"
      data-label-position={labelPosition}
      data-readonly={isReadOnly || undefined}
      data-disabled={isDisabled || undefined}
      data-invalid={isInvalid || undefined}
      data-required={isRequired || undefined}
    >
      {field}
    </div>
  );
});
